import { useEffect } from 'react';
import { supabase } from '../supabase/client';
import { useAuth } from './store';

export function useAuthSessionRestore() {
  const { setUser, setSession, setLoading } = useAuth();

  useEffect(() => {
    let isMounted = true;
    
    // Initial session check
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!isMounted) return;
        
        if (error) {
          console.error('Session error:', error);
          setUser(null);
          setSession(null);
          setLoading(false);
          return;
        }

        if (session?.user) {
          console.log('Found existing session for user:', session.user.id);
          setSession(session);
          
          // Fetch user profile from your DB
          const { data: profile, error: profileError } = await supabase
            .from('users')
            .select(`
              *,
              creator_profile:creator_profiles(*)
            `)
            .eq('id', session.user.id)
            .single();
            
          if (profileError) {
            console.error('Profile fetch error:', profileError);
            setUser(null);
            setSession(null);
          } else if (profile) {
            const mappedUser = {
              ...profile,
              status: profile.status || 'active'
            };
            setUser(mappedUser);
            console.log('Session restored for user:', mappedUser);
          }
        } else {
          console.log('No existing session found');
          setUser(null);
          setSession(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        setSession(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      console.log('Auth state changed:', event, session?.user?.id);
      
      if (event === 'SIGNED_IN' && session?.user) {
        setSession(session);
        
        // Don't fetch profile again if we already have it from sign-in
        // This prevents double fetching
        const currentUser = useAuth.getState().user;
        if (currentUser?.id === session.user.id) {
          console.log('User already set from sign-in, skipping profile fetch');
          return;
        }
        
        // Fetch profile for OAuth or other sign-in methods
        const { data: profile, error: profileError } = await supabase
          .from('users')
          .select(`
            *,
            creator_profile:creator_profiles(*)
          `)
          .eq('id', session.user.id)
          .single();
          
        if (profileError) {
          console.error('Profile fetch error on auth change:', profileError);
        } else if (profile) {
          const mappedUser = {
            ...profile,
            status: profile.status || 'active'
          };
          setUser(mappedUser);
          console.log('Auth change: user signed in and profile loaded:', mappedUser);
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('User signed out');
        setUser(null);
        setSession(null);
      }
    });

    // Initialize auth
    initializeAuth();
    
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [setUser, setSession, setLoading]);
} 